import { useId, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/core/cross';
import { media } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';

import { BlurredBackgroundImage } from '@/libraries/design/components/blurred-background-image/src/blurred-background-image';
import { BlurredBackgroundVideo } from '@/libraries/design/components/blurred-background-video/src/blurred-background-video';
import { Button } from '@/libraries/design/components/button/src/button';
import { Image } from '@/libraries/design/components/image/src/image';
import { Link } from '@/libraries/design/components/link/src/link';
import { ColumnLayout, wacToken } from '@/libraries/design/foundations';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

export type TakeoverBannerProps = {
	logoUrl: string;
	logoAlt?: string;
	dates: string;
	location: string;
	title: string;
	body?: string;
	buttonLabel: string;
	buttonHref: string;
	linkText: string;
	linkHref: string;
	dataEventProps?: DataEventProps;
	mediaType?: 'video' | 'image';
	videoUrl?: string;
	posterUrl?: string;
	videoDescription?: string;
	imageUrl?: string;
	imageAlt?: string;
	onClose?: () => void;
};

const CalendarIcon = () => {
	return (
		<svg
			width="18"
			height="24"
			viewBox="0 0 18 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			focusable="false"
		>
			<g clipPath="url(#clip0_453_18476)">
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M6.875 3.875H10.625V2H13.125V3.875H17.5V21.375H0V3.875H4.375V2H6.875V3.875ZM6.875 6.375V8.25H4.375V6.375H2.5V10.75H15V6.375H13.125V8.25H10.625V6.375H6.875Z"
					fill="white"
				/>
			</g>
			<defs>
				<clipPath id="clip0_453_18476">
					<rect width="17.5" height="24" fill="white" />
				</clipPath>
			</defs>
		</svg>
	);
};

const LocationIcon = () => {
	return (
		<svg
			width="14"
			height="24"
			viewBox="0 0 14 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			focusable="false"
		>
			<g clipPath="url(#clip0_453_18479)">
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M6.57503 21.0625L1.17169 13.2277C-1.83124 8.87341 1.28569 2.9375 6.57503 2.9375C11.8644 2.9375 14.9813 8.87341 11.9784 13.2277L6.57503 21.0625ZM6.57501 12.625C8.3009 12.625 9.70001 11.2259 9.70001 9.5C9.70001 7.77411 8.3009 6.375 6.57501 6.375C4.84912 6.375 3.45001 7.77411 3.45001 9.5C3.45001 11.2259 4.84912 12.625 6.57501 12.625Z"
					fill="white"
				/>
			</g>
			<defs>
				<clipPath id="clip0_453_18479">
					<rect width="13.15" height="24" fill="white" />
				</clipPath>
			</defs>
		</svg>
	);
};

export const TakeoverBanner = ({
	logoUrl,
	logoAlt,
	dates,
	location,
	title,
	body,
	buttonLabel,
	buttonHref,
	linkText,
	linkHref,
	dataEventProps,
	mediaType = 'video',
	videoUrl,
	posterUrl,
	videoDescription,
	imageUrl,
	imageAlt,
	onClose,
}: // eslint-disable-next-line complexity
TakeoverBannerProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isDismissed, setIsDismissed] = useState(false);
	const headingId = useId();
	const buttonId = useId();
	const linkId = useId();
	const closeLabel = intl.formatMessage({
		id: 'takeoverBanner.closeButtonLabel',
		defaultMessage: 'Close',
	});

	const handleClose = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'closeTakeoverBanner',
			source: 'takeoverBanner',
			attributes: {
				label: 'Close banner',
			},
		}).fire();
		setIsDismissed(true);
		onClose?.();
	};
	const shouldRenderImage = mediaType === 'image';

	if (isDismissed) {
		return null;
	}

	return (
		<UFOSegment name="takeover-banner">
			<div css={containerStyles}>
				<button type="button" css={closeButtonStyles} aria-label={closeLabel} onClick={handleClose}>
					<span css={closeButtonCircleStyles}>
						<span css={closeIconStyles}>
							<CrossIcon label="" size="medium" color="currentColor" />
						</span>
					</span>
				</button>
				<ColumnLayout layout="6-6" tabletLayout="6-6" mobileLayout="6-6" verticalAlignment="center">
					<div css={leftSideStyles}>
						<div css={logoAndTimePlaceDetailsStyles}>
							<Image src={logoUrl} alt={logoAlt ?? ''} css={logoImageStyles} />
							<div css={timePlaceDetailsStyles}>
								{dates && (
									<div css={timePlaceDetailStyles}>
										<span css={timePlaceDetailIconStyles}>
											<CalendarIcon />
										</span>
										<span className="body-md-bold">{dates}</span>
									</div>
								)}
								{location && (
									<div css={timePlaceDetailStyles}>
										<span css={timePlaceDetailIconStyles}>
											<LocationIcon />
										</span>
										<span className="body-md-bold">{location}</span>
									</div>
								)}
							</div>
						</div>
						<div css={copyGroupStyles}>
							<h1 className="card-title" id={headingId}>
								{title}
							</h1>
							{body && <p className="body-md">{body}</p>}
						</div>
						<div css={buttonAreaStyles}>
							<Button
								href={buttonHref}
								id={buttonId}
								size="small"
								variant="secondary"
								colorAgainst="black"
								dataEventProps={dataEventProps}
								ariaLabelledby={`${headingId} ${buttonId}`}
							>
								{buttonLabel}
							</Button>
							{linkText && linkHref && (
								<Link
									text={linkText}
									href={linkHref}
									id={linkId}
									type="primary"
									bgColor="bgBlackBlue"
									dataEventProps={dataEventProps}
									ariaLabelledby={`${headingId} ${linkId}`}
									aria-label={linkText}
								/>
							)}
						</div>
					</div>
					<div css={rightSideStyles}>
						{shouldRenderImage && imageUrl ? (
							<BlurredBackgroundImage src={imageUrl} alt={imageAlt ?? ''} />
						) : shouldRenderImage ? null : videoUrl ? (
							<BlurredBackgroundVideo
								src={videoUrl}
								poster={posterUrl}
								videoDescription={videoDescription}
							/>
						) : posterUrl ? (
							<BlurredBackgroundImage src={posterUrl} alt={videoDescription ?? ''} />
						) : null}
					</div>
				</ColumnLayout>
			</div>
		</UFOSegment>
	);
};

const containerStyles = css({
	overflow: 'hidden',
	margin: token('space.0'),
	position: 'relative',
	display: 'flex',
	height: '380px',
	maxHeight: 'none',
	alignItems: 'stretch',
	justifyContent: 'center',
	[media.above.lg]: {
		height: '480px',
	},
	[media.below.sm]: {
		height: 'auto',
		maxHeight: '420px',
		overflowX: 'hidden',
		overflowY: 'auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		'--cl-gap': token('space.0'),
		height: '100%',
		maxWidth: 'none',
		paddingInlineStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		[media.below.sm]: {
			height: 'auto',
			maxHeight: 'inherit',
		},
	},
});

const closeButtonStyles = css({
	appearance: 'none',
	position: 'absolute',
	insetBlockStart: token('space.200'),
	insetInlineEnd: token('space.200'),
	zIndex: 3,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	flex: '0 0 auto',
	aspectRatio: '1 / 1',
	inlineSize: token('space.500'),
	blockSize: token('space.500'),
	minInlineSize: token('space.500'),
	minBlockSize: token('space.500'),
	maxInlineSize: token('space.500'),
	maxBlockSize: token('space.500'),
	padding: token('space.0'),
	border: 'none',
	borderRadius: '100%',
	backgroundColor: 'transparent',
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});

const closeButtonCircleStyles = css({
	backgroundColor: wacToken('color.neutral.light300'),
	borderRadius: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	boxSizing: 'border-box',
	flex: '0 0 auto',
	aspectRatio: '1 / 1',
	inlineSize: token('space.500'),
	blockSize: token('space.500'),
	minInlineSize: token('space.500'),
	minBlockSize: token('space.500'),
	maxInlineSize: token('space.500'),
	maxBlockSize: token('space.500'),
	[media.below.lg]: {
		inlineSize: token('space.400'),
		blockSize: token('space.400'),
		minInlineSize: token('space.400'),
		minBlockSize: token('space.400'),
		maxInlineSize: token('space.400'),
		maxBlockSize: token('space.400'),
	},
	'&:hover': {
		backgroundColor: wacToken('color.neutral.light400'),
	},
	'&:active': {
		backgroundColor: wacToken('color.neutral.light400'),
	},
});

const closeIconStyles = css({
	color: wacToken('color.primary.black'),
	display: 'flex',
	lineHeight: 0,
});

const leftSideStyles = css({
	position: 'relative',
	display: 'flex',
	gap: token('space.300'),
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'flex-start',
	alignSelf: 'stretch',
	boxSizing: 'border-box',
	backgroundColor: wacToken('color.primary.black'),
	paddingBlock: token('space.500'),
	paddingInline: token('space.500'),
	[media.below.sm]: {
		gridColumn: '1 / -1',
	},
	[media.below.lg]: {
		paddingInline: token('space.300'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h1.card-title': {
		color: wacToken('color.neutral.light0'),
		margin: token('space.0'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'p.body-md': {
		color: wacToken('color.neutral.light0'),
		margin: token('space.0'),
	},
});

const rightSideStyles = css({
	position: 'relative',
	display: 'grid',
	alignSelf: 'stretch',
	width: '100%',
	[media.below.sm]: {
		display: 'none',
	},
});

const timePlaceDetailsStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	columnGap: token('space.400'),
	rowGap: token('space.100'),
	fontSize: '1rem',
});

const timePlaceDetailStyles = css({
	color: wacToken('color.neutral.light0'),
	alignItems: 'center',
	display: 'flex',
	gap: token('space.150'),
	whiteSpace: 'nowrap',
});

const timePlaceDetailIconStyles = css({
	alignItems: 'center',
	display: 'inline-flex',
	flexShrink: 0,
	justifyContent: 'center',
	lineHeight: 0,
	width: '18px',
});

const copyGroupStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	width: '100%',
});

const buttonAreaStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	columnGap: token('space.250'),
	rowGap: token('space.250'),
	alignItems: 'center',
});

const logoAndTimePlaceDetailsStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

const logoImageStyles = css({
	alignSelf: 'flex-start',
	display: 'block',
	height: '28px',
	maxWidth: '100%',
	width: 'auto',
	[media.above.md]: {
		height: '40px',
	},
	[media.above.lg]: {
		height: '50px',
	},
});

TakeoverBanner.displayName = 'TakeoverBanner';
